import React, { useState, useEffect } from 'react';
import { Chart } from 'react-google-charts';
import { Link } from 'react-router-dom';
import DashboardBox from 'ui/dashboard/Box';
import DashboardBoxActions from 'ui/dashboard/BoxActions';
import DashboardBoxContent from 'ui/dashboard/BoxContent';
import DashboardBoxTitle from 'ui/dashboard/BoxTitle';
import Dashboard from 'ui/dashboard/Dashboard';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import SimpleTable from 'ui/Table';
import DownloadLink from 'ui/DownloadLink';
import Decimal from 'ui/formats/Decimal';
import Percent from 'ui/formats/Percent';
import Money from 'ui/formats/Money';
import Access from 'ui/Access';

import formatters from '../../../../formatters';
import { withAppContext } from '../../../AppContext';
import PaymentInfo from '../PaymentInfo';
import {
    loadBillingsChart,
    getPaymentMethod,
    getTransactions,
    getPaymentInfo,
} from '../services';
import { SubscriptionBox } from './summary/account';

const COLORS = {
    standard: '#4a6b90',
    advanced: '#6abb53',
};

const cantAccessTile = currentOrg => {
    return (
        currentOrg &&
        currentOrg.parent &&
        currentOrg.parent.type === 'reseller' &&
        !currentOrg.parent.is_super_pb_partner
    );
};

const BOXES = [
    {
        name: 'summary',
        title: 'Account summary',
        content: <SummaryBox />,
        access: Access.ACCOUNT_DETAILS_SUMMARY,
    },
    {
        name: 'subscription',
        title: 'Subscription summary',
        content: <SubscriptionBox />,
        access: [
            Access.ACCOUNT_DETAILS_SUBSCRIPTION_OWNER,
            Access.ACCOUNT_DETAILS_SUBSCRIPTION_USER,
        ],
    },
    {
        name: 'user_counts',
        title: 'User counts',
        content: <CounterBox />,
        actions: [
            {
                label: 'View user counts',
                url: '/account/details/user_counts',
            },
        ],
        access: Access.ACCOUNT_DETAILS_USER_COUNTS,
    },
    {
        name: 'transactions',
        title: 'Transactions',
        content: <TransactionsBox />,
        actions: [
            {
                label: 'View transactions',
                url: '/account/details/transactions',
            },
        ],
        access: currentOrg => {
            if (cantAccessTile(currentOrg)) {
                return null;
            }
            return Access.ACCOUNT_DETAILS_TRANSACTIONS;
        },
    },
    {
        name: 'payment_method',
        title: 'How you pay',
        content: <PaymentMethodBox />,
        actions: [
            {
                label: 'Manage payment methods',
                url: '/account/details/payment_method',
            },
        ],
        access: Access.ACCOUNT_DETAILS_PAYMENT_METHOD,
    },
    {
        name: 'settings',
        title: 'Settings',
        content: <SettingsBox />,
        actions: [
            {
                label: 'Manage settings',
                url: '/account/details/settings',
            },
        ],
        access: currentOrg => {
            if (cantAccessTile(currentOrg)) {
                return null;
            }
            return Access.ACCOUNT_DETAILS_PAYMENT_SETTINGS;
        },
    },
];

function SummaryBox() {
    const [accountSummary, setAccountSummary] = useState([]);
    const [total, setTotal] = useState([]);

    useEffect(() => {
        getTransactions({ account_summary: true }).then(data => {
            let currerntAccountData = Object.entries(data.summary.data).map(
                ([subscription, value]) => {
                    return [
                        subscription,
                        value.companies,
                        value.companies_percent,
                        value.users,
                        value.users_percent,
                    ];
                }
            );

            setAccountSummary(currerntAccountData);
            if (data.summary.data.length > 0) {
                setTotal([
                    'Total',
                    data.summary.total.companies,
                    null,
                    data.summary.total.users,
                    null,
                    null,
                ]);
            }
        });
    }, []);

    const colorFomater = (formater = val => val) => (value, row) => (
        <span style={{ color: COLORS[row[0].toLowerCase()] }}>
            {formater(value)}
        </span>
    );

    const options = {
        compact: true,
        columns: [
            { styles: { align: 'left' }, format: colorFomater() },
            {
                format: colorFomater(value => (
                    <Decimal value={Number(value)} />
                )),
            },
            {
                format: colorFomater(value => (
                    <Percent value={Number(value)} />
                )),
            },
            { format: colorFomater(value => <Decimal value={value} />) },
            {
                format: colorFomater(value => (
                    <Percent value={Number(value)} />
                )),
            },
        ],
        header: ['Subscription', 'Companies', '% Total', 'Users', '% Total'],
        footer: total,
    };

    return <SimpleTable data={accountSummary} options={options} />;
}

function SettingsBox() {
    const [companyProfile, setCompanyProfile] = useState(null);

    useEffect(() => {
        getPaymentInfo().then(data => {
            setCompanyProfile(data.company_profile);
        });
    }, []);

    return companyProfile ? (
        <Typography>
            {companyProfile.company_name}
            <br />
            {companyProfile.address1}
            <br />
            {`${companyProfile.city}, ${companyProfile.postal_code} `}
        </Typography>
    ) : null;
}

function PaymentMethodBox() {
    const [paymentInfo, setPaymentInfo] = useState(null);

    useEffect(() => {
        getPaymentMethod().then(payment_method => {
            setPaymentInfo({
                ...payment_method,
            });
        });
    }, []);

    return paymentInfo ? <PaymentInfo payment={paymentInfo} /> : null;
}

function CounterBox() {
    const [chartData, setChartData] = useState(null);
    const [chartOptions, setChartOptions] = useState({});

    useEffect(() => {
        loadBillingsChart().then(billings => {
            const headers = ['Month', ...billings.levels].map(h =>
                h.replace(/\w/, c => c.toUpperCase())
            );
            const data = billings.data.map(item => {
                const values = billings.levels.map(name => item[name]);
                const month = new Date(item.date).toLocaleString('en-us', {
                    month: 'short',
                });
                return [month, ...values];
            });

            const seriesColors = billings.levels.reduce((acc, name, index) => {
                acc[index] = { color: COLORS[name] };
                return acc;
            }, {});

            const options = {
                chartArea: {
                    left: 25,
                    top: 5,
                    width: '70%',
                    height: '80%',
                },
                series: seriesColors,
                legend: {
                    alignment: 'start',
                },
            };

            setChartData([headers, ...data]);
            setChartOptions(options);
        });
    }, []);
    return chartData ? (
        <Chart
            width={'450px'}
            height={'130px'}
            chartType="LineChart"
            loader={<div>Loading Chart</div>}
            data={chartData}
            options={chartOptions}
            rootProps={{ 'data-testid': '0' }}
        />
    ) : (
        <div>Loading Chart</div>
    );
}

function TransactionsBox() {
    const [transactions, setTransactions] = useState([]);

    const options = {
        compact: true,
        borders: false,
        columns: [
            {
                hidden: true,
            },
            {
                styles: { align: 'left' },
                format: value => (
                    <Typography color="secondary">{value}</Typography>
                ),
            },
            {
                styles: { align: 'right' },
            },
            {
                styles: { width: '50px' },
                format: value => {
                    return value ? (
                        <DownloadLink
                            href={`/api/partner/transactions/invoice/${value}`}
                        />
                    ) : null;
                },
            },
        ],
    };

    useEffect(() => {
        getTransactions({ transactions: true }).then(data => {
            const row = data.billings.map(bill => {
                const date = new Date(bill.date);
                const startDate = `${formatters.date(date, 'MMM')} 1`;
                const today = `${formatters.date(date, 'D')}`;
                const year = `${formatters.date(date, 'YYYY')}`;
                const title = `${startDate} - ${today}, ${year}`;

                return [
                    1,
                    title,
                    <Money
                        amount={Number(bill.total_amount)}
                        currency={bill.currency}
                    />,
                    bill.payment.invoice_number,
                ];
            });
            setTransactions(row);
        });
    }, []);

    return <SimpleTable data={transactions} options={options} />;
}

export default withAppContext(function Summary({ app }) {
    const canViewTile = tabAccess => {
        const { currentUser } = app;
        if (typeof tabAccess === 'function') {
            tabAccess = tabAccess(currentUser.organizations[0]);
        }
        return app.canView(tabAccess);
    };

    const accessFilter = box => canViewTile(box.access);
    const boxes = BOXES.filter(accessFilter);
    return (
        <Dashboard>
            {boxes.map(({ name, title, content, actions }) => (
                <DashboardBox key={name}>
                    <DashboardBoxTitle>{title}</DashboardBoxTitle>
                    <DashboardBoxContent>{content}</DashboardBoxContent>
                    {actions && actions.length && (
                        <DashboardBoxActions>
                            {actions.map(({ label, url }) => (
                                <Button
                                    color="secondary"
                                    key={url}
                                    component={Link}
                                    to={url}
                                    underline="none"
                                >
                                    {label}
                                </Button>
                            ))}
                        </DashboardBoxActions>
                    )}
                </DashboardBox>
            ))}
        </Dashboard>
    );
});
